/**
 * useAccessMapFiltering Hook
 * 
 * Custom hook for filtering items based on access map rules.
 * Provides filtering functions for different tabs and access scenarios.
 * 
 * This hook provides:
 * - Tab-based filtering (myProjects, discovery)
 * - Role-based filtering
 * - Public access filtering
 */

import { useCallback, useMemo } from 'react';
import { filterByTab, filterByMyItems, filterByPublicItems } from '../../utils/accessMapFilters';
import { TAB_NAMES } from '../../utils/accessMapConstants';

/**
 * Hook for filtering items based on access map rules
 * 
 * @param {string} currentUsername - Current user's username
 * @param {Object} currentUser - Optional current user object with id and username (for ID matching)
 * @returns {Object} - Object with filtering functions
 *   - filterByTab: Filter items by tab name
 *   - filterMyItems: Filter items where user has ANY explicit role (OWNER, CONTRIBUTOR, or VIEWER)
 *   - filterPublicItems: Filter public items where user has NO explicit role
 */
export const useAccessMapFiltering = (currentUsername, currentUser = null) => {
  /**
   * Filters items based on the active tab
   * @param {Array} items - Array of items to filter
   * @param {string} activeTab - Active tab name
   * @returns {Array} - Filtered array of items
   */
  const filterByTabName = useCallback((items, activeTab) => {
    return filterByTab(items, activeTab, currentUsername, currentUser);
  }, [currentUsername, currentUser]);

  /**
   * Filters items where user has ANY explicit role (OWNER, CONTRIBUTOR, or VIEWER)
   * @param {Array} items - Array of items to filter
   * @returns {Array} - Filtered array of items
   */
  const filterMyItems = useCallback((items) => {
    return filterByMyItems(items, currentUsername, currentUser);
  }, [currentUsername, currentUser]);

  /**
   * Filters public items where user has NO explicit role
   * @param {Array} items - Array of items to filter
   * @returns {Array} - Filtered array of items
   */
  const filterPublicItems = useCallback((items) => {
    return filterByPublicItems(items, currentUsername, currentUser);
  }, [currentUsername, currentUser]);

  return useMemo(() => ({
    filterByTab: filterByTabName,
    filterMyItems,
    filterPublicItems,
    // Convenience properties for common tab names
    TAB_NAMES
  }), [filterByTabName, filterMyItems, filterPublicItems]);
};

